import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { BookOpen, FileText, Lock, Search, X } from 'lucide-react';
import { fetchCookbooks, Cookbook } from '@/lib/cookbooks';
import { useAuth } from '@/contexts/AuthContext';
import LoadingSpinner from '@/components/LoadingSpinner';
import styles from './CookbooksPage.module.css';

const SUBSCRIBE_URL = 'https://peapilpublishing.com/pages/peapil-health';
const FREE_COOKBOOK_ID = 'mediterranean-refresh';

function CookbookCard({ cookbook, isLocked }: { cookbook: Cookbook; isLocked: boolean }) {
  const { hasFullAccess } = useAuth();
  const showLock = !cookbook.isFreePreview && !hasFullAccess;

  if (isLocked) {
    return (
      <div className={`${styles.card} ${styles.cardLocked}`}>
        <div className={styles.imageWrapper}>
          <img
            src={cookbook.coverImage}
            alt={cookbook.title}
            className={`${styles.image} ${styles.imageLocked}`}
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/placeholder-cookbook.png';
            }}
          />
          <div className={styles.lockOverlay}>
            <Lock size={32} />
            <span>Premium</span>
          </div>
        </div>
        <div className={styles.cardContent}>
          <h3 className={styles.cardTitle}>{cookbook.title}</h3>
          <p className={styles.author}>{cookbook.author}</p>
          <div className={styles.cardMeta}>
            <span className={styles.pages}>
              <FileText size={16} />
              {cookbook.pageCount} pages
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Link to={`/cookbooks/${cookbook.id}`} className={styles.card}>
      <div className={styles.imageWrapper}>
        <img
          src={cookbook.coverImage}
          alt={cookbook.title}
          className={styles.image}
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = '/placeholder-cookbook.png';
          }}
        />
        {showLock && <div className={styles.lockBadge}>Premium</div>}
      </div>
      <div className={styles.cardContent}>
        <h3 className={styles.cardTitle}>{cookbook.title}</h3>
        <p className={styles.author}>{cookbook.author}</p>
        <div className={styles.cardMeta}>
          <span className={styles.pages}>
            <FileText size={16} />
            {cookbook.pageCount} pages
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function CookbooksPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const { hasFullAccess } = useAuth();
  
  const { data: cookbooks, isLoading, error } = useQuery({
    queryKey: ['cookbooks'],
    queryFn: fetchCookbooks,
  });

  const filteredCookbooks = useMemo(() => {
    if (!cookbooks) return [];
    return cookbooks.filter((cookbook) =>
      searchQuery === '' ||
      cookbook.title.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [cookbooks, searchQuery]);

  if (isLoading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className={styles.errorState}>
        <p>Unable to load cookbooks. Please try again.</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>
          <BookOpen size={28} />
          Cookbook Library
        </h1>
        <p className={styles.subtitle}>Browse our collection of health and wellness guides</p>
      </header>

      <div className={styles.searchWrapper}>
        <Search size={20} className={styles.searchIcon} />
        <input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.searchInput}
        />
        {searchQuery && (
          <button className={styles.clearButton} onClick={() => setSearchQuery('')}>
            <X size={18} />
          </button>
        )}
      </div>

      {!hasFullAccess && (
        <div className={styles.upgradePrompt}>
          <Lock size={24} />
          <div>
            <h3>Unlock All Cookbooks</h3>
            <p>Subscribe to access our complete cookbook library with full PDFs.</p>
          </div>
          <a href={SUBSCRIBE_URL} target="_blank" rel="noopener noreferrer" className={styles.upgradeButton}>
            Subscribe Now
          </a>
        </div>
      )}

      <div className={styles.grid}>
        {filteredCookbooks.map((cookbook) => {
          const isLocked = !hasFullAccess && cookbook.id !== FREE_COOKBOOK_ID && !cookbook.isFreePreview;
          return (
            <CookbookCard key={cookbook.id} cookbook={cookbook} isLocked={isLocked} />
          );
        })}
      </div>

      {filteredCookbooks.length === 0 && (
        <div className={styles.emptyState}>
          <BookOpen size={48} />
          <p>No cookbooks available at the moment.</p>
        </div>
      )}
    </div>
  );
}
